import { useEffect } from 'react';
import { useQuery } from '@tanstack/react-query';
import { commandCenterApi } from '../api';
import { useCommandCenter } from '../store';
import { StatusDot } from './LeftRail';

export function DetailPanel() {
  const assetId = useCommandCenter((s) => s.selectedAssetId);
  const asset = useCommandCenter((s) => (assetId ? s.assets[assetId] : null));
  const setRightPanel = useCommandCenter((s) => s.setRightPanel);
  const setTrail = useCommandCenter((s) => s.setTrail);

  const today = new Date();
  const start = new Date(today);
  start.setHours(0, 0, 0, 0);

  const trailQuery = useQuery({
    queryKey: ['asset-trail', assetId, start.toISOString()],
    queryFn: () =>
      commandCenterApi.assetTrail(assetId!, {
        start: start.toISOString(),
        end: today.toISOString(),
        zoom: 14,
      }),
    enabled: !!assetId,
  });

  useEffect(() => {
    if (trailQuery.data) setTrail(trailQuery.data);
    return () => setTrail(null);
  }, [trailQuery.data, setTrail]);

  if (!asset) return null;

  return (
    <div className="flex h-full flex-col border-l border-cc-border bg-cc-surface text-cc-fg">
      <div className="flex items-center justify-between border-b border-cc-border px-3 py-2">
        <button
          onClick={() => setRightPanel('alerts')}
          className="font-mono text-[10px] uppercase text-cc-muted hover:text-cc-accent"
        >
          ← Back to alerts
        </button>
        <button
          className="font-mono text-[10px] uppercase text-cc-muted hover:text-cc-accent"
          title="Pop out (Phase 2)"
          disabled
        >
          ⧉ Pop out
        </button>
      </div>

      <div className="border-b border-cc-border p-3">
        <div className="flex items-start gap-2">
          <StatusDot status={asset.status} />
          <div>
            <h2 className="font-mono text-sm font-semibold uppercase">{asset.name}</h2>
            <p className="font-mono text-[10px] text-cc-muted">
              {asset.code} · {asset.plate_number ?? 'no plate'} · {asset.asset_type_slug}
            </p>
          </div>
        </div>
        <div className="mt-3 grid grid-cols-2 gap-3 font-mono text-xs">
          <Stat
            label="Speed"
            value={asset.speed_kmh != null ? `${asset.speed_kmh.toFixed(0)} km/h` : '—'}
          />
          <Stat
            label="Heading"
            value={asset.heading != null ? `${asset.heading.toFixed(0)}°` : '—'}
          />
          <Stat label="Lat" value={asset.lat != null ? asset.lat.toFixed(5) : '—'} />
          <Stat label="Lng" value={asset.lng != null ? asset.lng.toFixed(5) : '—'} />
        </div>
        <p className="mt-3 font-mono text-[10px] text-cc-muted">
          Last fix: {asset.last_fix_at ? new Date(asset.last_fix_at).toLocaleString() : 'never'}
        </p>
      </div>

      <section className="border-b border-cc-border p-3">
        <h3 className="font-mono text-[10px] font-semibold uppercase tracking-wider text-cc-muted">
          Today's Trail
        </h3>
        {trailQuery.isLoading ? (
          <p className="mt-2 font-mono text-xs text-cc-muted">Loading…</p>
        ) : trailQuery.data ? (
          <p className="mt-2 font-mono text-xs text-cc-muted">
            {trailQuery.data.decimated_count} of {trailQuery.data.raw_count} points · tolerance{' '}
            {trailQuery.data.tolerance_m.toFixed(1)} m
          </p>
        ) : (
          <p className="mt-2 font-mono text-xs text-cc-muted">[ NO DATA ]</p>
        )}
      </section>

      <section className="flex-1 overflow-auto p-3">
        <h3 className="font-mono text-[10px] font-semibold uppercase tracking-wider text-cc-muted">
          Activity (M7)
        </h3>
        <p className="mt-2 font-mono text-xs text-cc-muted">
          Work-unit / duty event feed lands with M7 operations module.
        </p>
      </section>
    </div>
  );
}

function Stat({ label, value }: { label: string; value: string }) {
  return (
    <div>
      <span className="block text-[10px] uppercase text-cc-muted">{label}</span>
      <span className="text-sm tabular-nums">{value}</span>
    </div>
  );
}
